/**
 * 国际电话区号：把"一个号码字符串"拆成「区号 + 本地号码」两段，再拼回去。
 *
 * 为什么要有这一对函数：库里存的仍然是**一个字符串**（`SiteContact.phone` /
 * `whatsapp`），前台、结构化数据、`tel:` 链接全从它派生，改存储形态会牵动
 * 三处调用方。但让客户在一个框里手打 `+86 138 0013 8000` 是这一页最容易
 * 出错的地方 —— 漏了 `+` 看不出、多打一个空格看不出、打成 `0086` 也看不出。
 * 所以界面上拆成「先选区号、再填号码」，库里那一个字符串由这里拼回来。
 *
 * 拆分是**有损的解析**（`+86 574 8712 3456` 里只能靠区号表判断哪几位是区号），
 * 所以规则只有一条：认不出来就别硬拆 —— 整串留在号码框里、区号用默认值，
 * 不猜、不改客户写的字。
 */

export interface DialCode {
  /** 区号，纯数字（不含 `+`）。与库里拼出来的形态保持一致 */
  code: string;
  /** 地区名，仅用于下拉框显示 */
  name: string;
}

/**
 * 没选/没识别出区号时的默认值。
 *
 * 取 `+86` 而不是留空：这一页绝大多数客户在中国，默认帮他选好最省事；
 * 选错了在框里也看得见（左半边写着「中国 +86」），比留一个空下拉框好。
 */
export const DEFAULT_DIAL = '86';

/**
 * 常用国家/地区。
 *
 * 不是 ITU 全表（两百多项，下拉框会变成一堵墙），而是外贸站真正会遇到的那批。
 * 排序按「中国 → 亚洲 → 中东 → 欧洲 → 美洲 → 非洲 → 大洋洲」，
 * 不按字母：客户先找的是"我在不在里面"，而不是"Z 打头的那个"。
 *
 * 表外的区号不会丢：`splitDial` 认不出来时把它整个当成区号，
 * 下拉框也会临时补一项，客户照样能改。
 */
export const DIAL_CODES: readonly DialCode[] = [
  { code: '86', name: '中国' },
  { code: '852', name: '中国香港' },
  { code: '853', name: '中国澳门' },
  { code: '886', name: '中国台湾' },
  { code: '81', name: '日本' },
  { code: '82', name: '韩国' },
  { code: '65', name: '新加坡' },
  { code: '60', name: '马来西亚' },
  { code: '66', name: '泰国' },
  { code: '84', name: '越南' },
  { code: '62', name: '印度尼西亚' },
  { code: '63', name: '菲律宾' },
  { code: '91', name: '印度' },
  { code: '92', name: '巴基斯坦' },
  { code: '880', name: '孟加拉国' },
  { code: '94', name: '斯里兰卡' },
  { code: '977', name: '尼泊尔' },
  { code: '855', name: '柬埔寨' },
  { code: '856', name: '老挝' },
  { code: '95', name: '缅甸' },
  { code: '673', name: '文莱' },
  { code: '976', name: '蒙古' },
  { code: '998', name: '乌兹别克斯坦' },
  { code: '971', name: '阿联酋' },
  { code: '966', name: '沙特阿拉伯' },
  { code: '974', name: '卡塔尔' },
  { code: '965', name: '科威特' },
  { code: '968', name: '阿曼' },
  { code: '973', name: '巴林' },
  { code: '962', name: '约旦' },
  { code: '972', name: '以色列' },
  { code: '90', name: '土耳其' },
  { code: '44', name: '英国' },
  { code: '49', name: '德国' },
  { code: '33', name: '法国' },
  { code: '39', name: '意大利' },
  { code: '34', name: '西班牙' },
  { code: '31', name: '荷兰' },
  { code: '32', name: '比利时' },
  { code: '41', name: '瑞士' },
  { code: '43', name: '奥地利' },
  { code: '46', name: '瑞典' },
  { code: '47', name: '挪威' },
  { code: '45', name: '丹麦' },
  { code: '358', name: '芬兰' },
  { code: '353', name: '爱尔兰' },
  { code: '351', name: '葡萄牙' },
  { code: '48', name: '波兰' },
  { code: '420', name: '捷克' },
  { code: '421', name: '斯洛伐克' },
  { code: '36', name: '匈牙利' },
  { code: '40', name: '罗马尼亚' },
  { code: '359', name: '保加利亚' },
  { code: '30', name: '希腊' },
  { code: '385', name: '克罗地亚' },
  { code: '381', name: '塞尔维亚' },
  { code: '386', name: '斯洛文尼亚' },
  { code: '370', name: '立陶宛' },
  { code: '371', name: '拉脱维亚' },
  { code: '372', name: '爱沙尼亚' },
  { code: '380', name: '乌克兰' },
  { code: '375', name: '白俄罗斯' },
  { code: '7', name: '俄罗斯' },
  /* 不带空格：下拉框里最长的一项，多两个空格就要把这一栏再撑宽 10px */
  { code: '1', name: '美国/加拿大' },
  { code: '52', name: '墨西哥' },
  { code: '55', name: '巴西' },
  { code: '54', name: '阿根廷' },
  { code: '56', name: '智利' },
  { code: '51', name: '秘鲁' },
  { code: '57', name: '哥伦比亚' },
  { code: '58', name: '委内瑞拉' },
  { code: '593', name: '厄瓜多尔' },
  { code: '506', name: '哥斯达黎加' },
  { code: '507', name: '巴拿马' },
  { code: '20', name: '埃及' },
  { code: '27', name: '南非' },
  { code: '234', name: '尼日利亚' },
  { code: '254', name: '肯尼亚' },
  { code: '212', name: '摩洛哥' },
  { code: '61', name: '澳大利亚' },
  { code: '64', name: '新西兰' },
];

const KNOWN = new Set(DIAL_CODES.map((item) => item.code));

/**
 * 少数国家（意大利、圣马力诺）的国际号码**保留**国内的前导 0，
 * 去掉反而打不通。其余国家那个 0 是长途前缀，国际格式里本来就不写。
 */
const KEEPS_TRUNK_ZERO = new Set(['39', '378']);

/** 本地号码至少要这么多个数字，少了就不像号码，不拆 */
const MIN_LOCAL_DIGITS = 6;

export interface SplitPhone {
  /** 区号（纯数字）。识别不出来时是 `DEFAULT_DIAL` */
  dial: string;
  /** 区号之后的本地号码，保留客户原来写的空格与连字符 */
  local: string;
}

/** 落库形态：`display` 保留空格分组（电话），`digits` 只留数字（WhatsApp） */
export type PhoneMode = 'display' | 'digits';

/** 号码框的编辑态：客户眼下看到的两个框 */
export interface PhoneDraft {
  dial: string;
  local: string;
}

/** 从字符串里数出前 n 个数字 */
function leadingDigits(value: string, count: number): string {
  let out = '';
  for (const ch of value) {
    if (out.length >= count) break;
    if (ch >= '0' && ch <= '9') out += ch;
  }
  return out;
}

/** 跳过前 count 个数字之后的剩余部分 */
function cutAfterDigits(value: string, count: number): string {
  let seen = 0;
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i]!;
    if (ch >= '0' && ch <= '9') {
      seen += 1;
      if (seen === count) return value.slice(i + 1);
    }
  }
  return '';
}

/**
 * 找区号：从长到短试 3/2/1 位。
 *
 * 必须从长往短试：`852`（香港）先于 `85`、`880`（孟加拉）先于 `88`；
 * 反过来会把香港号码切成 `85` + `2…`，拼回去虽然一样，
 * 但客户在下拉框里看到的是"区号 85" —— 一个不存在的区号。
 *
 * `minLength` 给 2 的情形是**没有国际前缀**的裸号码（`8613800138000`）：
 * 单数字区号（`1` 美国、`7` 俄罗斯）在这里不能认 —— 中国手机号
 * `13800138000` 会正好被切成 `+1` + `3800138000`，把客户指到美国去。
 */
function matchDial(head: string, minLength = 1): string | null {
  for (const length of [3, 2, 1]) {
    if (length < minLength) break;
    const candidate = leadingDigits(head, length);
    if (candidate.length === length && KNOWN.has(candidate)) return candidate;
  }
  return null;
}

/**
 * 拆分库里的号码。
 *
 * 三种写法都认：`+86 138…`、`0086 138…`（IDD 前缀等价于 `+`）、`86 138…`。
 * 都不像（老数据里只有 `0574 8712 3456`）时**不动它**：区号给默认值、
 * 整串留在号码框里。猜区号是危险的 —— 猜错会把客户指向另一个国家的号码，
 * 而他在界面上看不出来。
 */
export function splitDial(raw: string | null | undefined): SplitPhone {
  const value = (raw ?? '').trim();
  if (!value) return { dial: DEFAULT_DIAL, local: '' };

  const international = value.startsWith('+') || /^00\d/.test(value);

  if (!international) {
    const dial = matchDial(value, 2);
    const rest = dial ? cutAfterDigits(value, dial.length).trim() : '';
    if (dial && rest.replace(/\D/g, '').length >= MIN_LOCAL_DIGITS) {
      return { dial, local: rest };
    }
    return { dial: DEFAULT_DIAL, local: value };
  }

  const body = value.startsWith('+') ? value.slice(1) : value.slice(2);
  // 带 `+` 的号码不存在"切错国家"的问题：认不出就按前三位当区号，
  // 至少保证「拼回去」与「客户写的」一字不差
  const dial = matchDial(body) ?? leadingDigits(body, 3);
  if (!dial) return { dial: DEFAULT_DIAL, local: value };
  return { dial, local: cutAfterDigits(body, dial.length).trim() };
}

/**
 * 号码框里收到的输入 → 只留本地号码那一段。
 *
 * 需要这一步是因为粘贴：客户从名片、WhatsApp 资料页、报价单里复制过来的是
 * **整串**（`+86 138 0013 8000`、`0086 138…`、`8613800138000`）。
 * 直接拼区号会得到 `+86 8613800138000` —— 一个看起来很正常、拨出去是空号的
 * 号码。剥掉重复的国家码之后才拼。
 *
 * 剩下的和区号一样短于一个号码时不动它（宁可让客户自己发现，
 * 也不要把真实号码里的前几位当成区号吃掉）。
 */
export function localPartOf(raw: string, dial: string): string {
  let body = raw.trim();
  if (body.startsWith('+')) body = body.slice(1);
  else if (/^00\d/.test(body)) body = body.slice(2);

  const code = dial.replace(/\D/g, '');
  if (!code) return body;

  const digits = body.replace(/\D/g, '');
  if (!digits.startsWith(code)) return body;
  const rest = cutAfterDigits(body, code.length).trim();
  if (rest.replace(/\D/g, '').length < MIN_LOCAL_DIGITS) return body;
  return rest;
}

/** 去掉国内长途前缀的 0；整串都是 0 时原样留着，别让输入框把刚敲的字吃掉 */
function trimTrunkZero(dial: string, local: string): string {
  if (KEEPS_TRUNK_ZERO.has(dial)) return local;
  const stripped = local.replace(/^0+/, '');
  return stripped === '' ? local : stripped;
}

/**
 * 拼回库里那一个字符串。
 *
 * `display`：给「电话」用，保留客户写的空格分组 → `+86 574 8712 3456`；
 * `digits`：给 WhatsApp 用，只留数字 → `8613800138000`（`wa.me` 只收这个）。
 *
 * 本地号码为空时返回 `null`：只选了区号不算填了号码，
 * 存一个 `+86` 会让页脚出现一个拨不通的号码。
 */
export function joinDial(
  dial: string,
  local: string,
  mode: PhoneMode = 'display',
): string | null {
  const code = dial.replace(/\D/g, '') || DEFAULT_DIAL;

  if (mode === 'digits') {
    const digits = trimTrunkZero(code, local.replace(/\D/g, ''));
    return digits === '' ? null : `${code}${digits}`;
  }

  const cleaned = trimTrunkZero(
    code,
    local
      .replace(/[^\d\s()-]/g, '')
      .replace(/\s+/g, ' ')
      .trim(),
  ).trim();
  if (cleaned.replace(/[\s()-]/g, '') === '') return null;
  return `+${code} ${cleaned}`;
}

/** 下拉框里那一行字。表外区号没有地区名，只显示 `+xxx` */
export function dialLabel(item: DialCode): string {
  return item.name ? `${item.name} +${item.code}` : `+${item.code}`;
}

// ── 编辑态 ────────────────────────────────────────────────────────────────
//
// 号码框里显示什么，**不能**由落库值反解（`splitDial(value).local`）。
//
// 反解是有损的：电话和 WhatsApp 在库里都是一个字符串，区号和本地号挤在一起，
// 只能靠「剩下的位数够不够一个号码」判断哪几位是区号（见 `MIN_LOCAL_DIGITS`）。
// 客户刚敲第一个字时，本地号还不够长 —— 反解会把整个 `86` + 敲进去的数字
// 一起当成号码显示，下一个字又在这个基础上再拼一次区号，于是：
//
//     敲 1 → 框里 861 → 敲 3 → 框里 868613 → 敲 8 → 框里 8686138 …
//
// 客户看到的是"一输入就变成 86"。所以编辑态自己存一份（`PhoneDraft`），
// 落库值只是它的派生物；只有外部换值（切站点、加载、恢复）才重新反解。

/** 落库值 → 编辑态。只在加载、切站点、撤销这类「外部换值」时调用 */
export function draftOf(value: string | null | undefined): PhoneDraft {
  const { dial, local } = splitDial(value);
  return { dial, local };
}

/**
 * 只认区号、不管本地号码：认得出就返回，认不出返回 `null`。
 *
 * 和 `splitDial` 的区别是"没认出来"这件事有独立信号 —— 粘贴 `+44 7911 123456`
 * 时要把下拉框跟着带到英国，而粘贴一个没有国家码的本地号（`7911123456`）
 * 不能把客户已经选好的英国弹回中国。`splitDial` 认不出时给的是默认区号，
 * 分不清"认出来了，就是 86"和"没认出来，用了默认值"。
 */
export function detectDial(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  if (!value) return null;
  const international = value.startsWith('+') || /^00\d/.test(value);
  const body = value.startsWith('+') ? value.slice(1) : international ? value.slice(2) : value;
  const dial = international ? (matchDial(body) ?? leadingDigits(body, 3)) : matchDial(body, 2);
  if (!dial) return null;
  if (!international) {
    const rest = cutAfterDigits(body, dial.length);
    if (rest.replace(/\D/g, '').length < MIN_LOCAL_DIGITS) return null;
  }
  return dial;
}

/**
 * 号码框收到一串新文本 → 新的编辑态 + 该落库的值。
 *
 * `pasted` 只在**确认是粘贴**时才为真（见组件里对 `inputType` 的判断）。
 * 逐字输入绝不能剥国家码：客户的号码本身就可能以 86 打头（`8686 8686`），
 * 敲到第 8 位时被当成"重复的国家码"吃掉，又是另一种"输不进去"。
 *
 * 整串进来时区号也跟着号码走：粘贴一个英国号，下拉框自己切到 +44，
 * 而不是把 44 当成号码接在中国的后面。
 */
export function typeDraft(
  draft: PhoneDraft,
  raw: string,
  mode: PhoneMode = 'display',
  pasted = false,
): { draft: PhoneDraft; value: string | null } {
  const international = /^\s*(\+|00\d)/.test(raw);
  const strip = pasted || international;
  const detected = strip ? detectDial(raw) : null;
  const dial = detected ?? draft.dial;
  const stripped = strip ? localPartOf(raw, dial) : raw;
  // 只敲了一个 `+` 或 `00` 时剥完是空串：宁可留着客户敲的字
  const local = strip && stripped.trim() === '' ? raw : stripped;
  return { draft: { dial, local }, value: joinDial(dial, local, mode) };
}

/**
 * 换区号 → 新的编辑态 + 该落库的值。
 *
 * 号码框里的字一个都不动，只换前缀：客户先填号码再想起选国家，
 * 是这一页最常见的顺序。
 */
export function switchDial(
  draft: PhoneDraft,
  nextDial: string,
  mode: PhoneMode = 'display',
): { draft: PhoneDraft; value: string | null } {
  const dial = nextDial || DEFAULT_DIAL;
  return { draft: { dial, local: draft.local }, value: joinDial(dial, draft.local, mode) };
}
